import { IconsProps, ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import { SearchFieldOption } from 'components/2-molecules/SearchFieldComponent/SearchFieldComponent';
import { Page } from 'context/PageContext';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { apiInstance } from '../services/Api';
import {
	FrontendDatasourceEvent,
	SearchComponent,
	SearchConfiguration,
	SearchSelectionResult,
} from './SearchConfiguration';

type DawaOptionData = Fund & {
	href?: string;
	postnr?: string;
	kvh?: string;
	kvhx?: string;
	addressDetail?: AddressDetailResponse;
};

export class ZipcodeAddressSearch implements SearchComponent {
	searchConfiguration: SearchConfiguration;
	secondarySearchConfiguration: SearchConfiguration;
	apiSearch: boolean;
	isZipcodeSearch: boolean;
	isZipcodeByAddressSearchHack: boolean;
	minInputLength: number;
	hasHadResult: boolean;
	searchIcon: IconsProps<ThemeUnion>['icon'];
	lastValue: string;
	latency: number;
	pageContext: Partial<Page>;
	tempOptionAddressHack: SearchFieldOption;
	searchType: string;

	constructor(
		searchConfiguration: SearchConfiguration,
		pageContext: Partial<Page>,
		secondarySearchConfiguration?: SearchConfiguration
	) {
		this.searchConfiguration = searchConfiguration;
		this.apiSearch = true;
		// Hack until SPD/TIA can make fire and have a zipcode without kvh/kvhx as calculation parameter
		this.isZipcodeByAddressSearchHack =
			searchConfiguration.frontendDatasourceEvent === FrontendDatasourceEvent.ZIPCODE_BY_ADDRESS;
		this.isZipcodeSearch =
			searchConfiguration.frontendDatasourceEvent === FrontendDatasourceEvent.ZIPCODE ||
			this.isZipcodeByAddressSearchHack;
		this.minInputLength = this.isZipcodeSearch && !this.isZipcodeByAddressSearchHack ? 1 : 2;
		this.searchIcon = 'map-pin';
		this.latency = 100;
		this.pageContext = pageContext;
		this.searchType = 'adresser';
		// marker for adding kvhx field in handleSecondaryChange
		this.secondarySearchConfiguration = secondarySearchConfiguration ?? {};
	}

	init = async (): Promise<SearchFieldOption[]> => {
		return undefined;
	};

	handleInputChange = async (value: string): Promise<SearchFieldOption[]> => {
		if (value?.trim().length >= this.minInputLength) {
			this.lastValue = value;
			await new Promise((res) => setTimeout(res, this.latency));

			if (this.lastValue !== value) {
				return [];
			}

			const options: SearchFieldOption[] = await refreshSearchResults(value, this.searchConfiguration)
				.then((res) => {
					const result: SearchFieldOption[] = [];
					res?.fund?.forEach((item) => {
						this.hasHadResult = true;
						result.push({
							label: item.titel,
							value: JSON.stringify(item),
						});
					});

					return result;
				})
				.catch((error) => {
					console.error(error);
					return [];
				});

			return options;
		}

		return [];
	};

	handleChange = async (
		option: SearchFieldOption,
		register?: UseFormRegister<FieldValues>
	): Promise<SearchSelectionResult> => {
		const data = parseOptionValue(option.value);

		// Progressive address search: street -> street+number -> final address
		if (shouldContinueAddressSearch(data, this.searchType)) {
			return {
				option,
				continueSearch: true,
				nextQuery: data.titel ?? '',
			};
		}

		if (data && (!this.isZipcodeSearch || this.isZipcodeByAddressSearchHack)) {
			await enrichAddressData(data, this.searchConfiguration.url);
			option.value = JSON.stringify(data);
			await submitAddressOutputFields(this.searchConfiguration, data.kvhx, option, register);

			if (this.isZipcodeByAddressSearchHack) {
				this.tempOptionAddressHack = { ...option };
				option.value = data.postnr ?? data.addressDetail?.adresse?.husnummer?.postnummer?.postnr ?? '';
			}
		}

		return { option, continueSearch: false };
	};

	handleChangeOld = async (option: SearchFieldOption): Promise<SearchFieldOption> => {
		if (!this.isZipcodeSearch || this.isZipcodeByAddressSearchHack) {
			// add kvh or kvhx from Dataforsyningen to address object
			const data = JSON.parse(option.value);
			if (data.href) {
				const resKvhx: any = await apiInstance().get(data.href);
				if (resKvhx) {
					data.kvhx = resKvhx.kvhx ?? resKvhx.kvh;
					option.value = JSON.stringify(data);
				}
			}
			if (this.isZipcodeByAddressSearchHack) {
				// HACK! keep address object in temp variable to be used in handleSecondaryChange for updating CUSTOMER_FULL_ADDRESS
				this.tempOptionAddressHack = { ...option };
				// HACK! use zipcode as value in selectedOption, but keep full address as Label
				option.value = data.postnr;
			}
		}
		return option;
	};

	handleSecondaryChange = async (
		option: SearchFieldOption,
		register: UseFormRegister<FieldValues>
	): Promise<void | string> => {
		// Dataforsyningen doesn't have kvh(x) on postal codes
		const outputFieldIds = this.searchConfiguration?.outputFields?.items?.map(
			(f) => f.content.uniqueIdentifier?.uid
		);

		let kvhx;
		try {
			kvhx = JSON.parse(option.value)?.kvhx || JSON.parse(this.tempOptionAddressHack?.value)?.kvhx;
		} catch (error) {
			// perhaps there is not always kvh(x) in Dataforsyningen address search (?)
			console.warn(error);
		}

		// add potential output fields, e.g. kvh/kvhx for zipcode search, or address for zipcode by address search, to form
		for (const outputFieldId of outputFieldIds ?? []) {
			if (outputFieldId.toUpperCase().includes('KVHX')) {
				await submitKvhx(outputFieldId, kvhx, register);
			} else {
				// all other fields will get the address, as this is a zipcode/address search
				await submitAddress(outputFieldId, this.tempOptionAddressHack, register);
			}
		}

		return kvhx ?? 'UNKNOWN_KVHX';
	};
}

const parseOptionValue = (value: string): DawaOptionData | undefined => {
	try {
		return JSON.parse(value);
	} catch {
		return undefined;
	}
};

const shouldContinueAddressSearch = (data: DawaOptionData | undefined, searchType: string): boolean => {
	return (
		data?.type === 'vejnavn' ||
		data?.type === 'navngivenvejpostnummer' ||
		(data?.type === 'husnummer' && searchType === 'adresser')
	);
};

const enrichAddressData = async (data: DawaOptionData, searchUrl: string | undefined): Promise<void> => {
	const detailUrl = getAddressDetailUrl(searchUrl, data.id);

	if (data.type === 'adresse' && detailUrl) {
		const addressDetail: AddressDetailResponse | undefined = await apiInstance().get(detailUrl);
		const kvhx = addressDetail?.adresse?.husnummer?.['x-abgroup']?.kvhx;

		data.addressDetail = addressDetail;
		if (kvhx) {
			data.kvhx = kvhx;
		}
		return;
	}

	if (data.href) {
		const resKvhx: Pick<DawaOptionData, 'kvh' | 'kvhx'> | undefined = await apiInstance().get(data.href);
		if (resKvhx) {
			data.kvhx = resKvhx.kvhx ?? resKvhx.kvh;
		}
	}
};

const submitAddressOutputFields = async (
	searchConfiguration: SearchConfiguration,
	kvhx: string | undefined,
	address: SearchFieldOption,
	register: UseFormRegister<FieldValues> | undefined
): Promise<void> => {
	if (!register) {
		return;
	}

	const outputFieldIds = searchConfiguration?.outputFields?.items?.map((f) => f.content.uniqueIdentifier?.uid);
	for (const outputFieldId of outputFieldIds ?? []) {
		if (outputFieldId.toUpperCase().includes('KVHX')) {
			await submitKvhx(outputFieldId, kvhx, register);
		} else {
			await submitAddress(outputFieldId, address, register);
		}
	}
};

const refreshSearchResults = async (
	value: string,
	searchConfiguration: SearchConfiguration
): Promise<AddressResponse> => {
	try {
		const results: AddressResponse = await apiInstance().get(
			searchConfiguration.url + 'soeg?tekst=' + encodeURI(value)
		);

		return results ?? { status: 'No results found', fund: [] };
	} catch (error) {
		throw new Error('Error fetching search results: ' + error);
	}
};

const getAddressDetailUrl = (searchUrl: string | undefined, addressId: string | undefined): string | undefined => {
	if (!searchUrl || !addressId) {
		return undefined;
	}

	const [urlWithoutQuery] = searchUrl.split('?');
	const baseUrl = urlWithoutQuery.replace(/\/?soeg\/?$/i, '').replace(/\/+$/g, '');

	return `${baseUrl}/${encodeURIComponent(addressId)}`;
};

const changeProessor = (
	item: Fund,
	searchType: string,
	searchConfiguration?: SearchConfiguration
): { keepSearching: boolean } => {
	if (
		item.type === 'vejnavn' ||
		item.type === 'navngivenvejpostnummer' ||
		(item.type === 'husnummer' && searchType === 'adresser')
	) {
		//return await refreshSearchResults(item.titel, searchConfiguration);
		return { keepSearching: true };
	} else {
		return { keepSearching: false };
	}
};

const submitKvhx = async (kvhxName: string, kvhx: string | undefined, register: UseFormRegister<FieldValues>) => {
	const { onChange } = register(kvhxName, { required: true });
	await onChange({
		target: {
			name: kvhxName,
			value: {
				id: kvhxName,
				value: kvhx ?? 'UNKNOWN_KVHX',
				label: kvhx ?? 'UNKNOWN_KVHX',
				stepTitle: kvhxName,
				inputLabel: kvhxName,
				type: 'SearchFieldBlock',
			},
		},
	});
};

const submitAddress = async (addressName: string, address, register: UseFormRegister<FieldValues>) => {
	const { onChange: onChangeAddress } = register(addressName, { required: true });
	await onChangeAddress({
		target: {
			name: addressName,
			value: {
				id: addressName,
				value: address.value,
				label: address.label,
				stepTitle: 'Adresse',
				type: 'SearchFieldBlock',
			},
		},
	});
};
